import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useKcToken } from "../../setup/auth/useKcToken";
import {
  EOrganizationState,
  useOrganizationState,
} from "../../setup/util/useOrganizationState";
import Spinner from "../common/Spinner";
import styles from "./styles.module.scss";
import { ISubmittedOrganization } from "./CreateOrganizationForm";
import PortalOnboardingPrompt from "./PortalOnboardingPrompt";
import OrganizationGateShell from "./OrganizationGateShell";
import OrganizationActivation from "./OrganizationActivation";
import OrganizationPending, {
  IReviewDetails,
  IReviewDocument,
} from "./OrganizationPending";
import OrganizationRejected from "./OrganizationRejected";
import {
  addressDraftFromSaved,
  addressSummary,
  bankDraftFromSaved,
  bankSummary,
} from "./activation/drafts";
import { ORGANIZATION_DOCUMENTS_STEP_ENABLED } from "./organizationDocuments.constants";
import {
  EDocumentsState,
  useOrganizationDocuments,
} from "./useOrganizationDocuments";

interface Props {
  children: React.ReactNode;
}

/**
 * Gates the portal dashboard behind the logged-in account's organization state:
 * no org yet -> portal onboarding prompt, PENDING -> activation (bank account, national address,
 * documents) until all three are on file, then the under-review screen,
 * REJECTED -> rejection + resubmit, VERIFIED -> render children as-is.
 *
 * Every gated state renders inside OrganizationGateShell, which reuses the app's own sidebar
 * and topbar, so the gate looks like the portal rather than a separate app.
 */
const OrganizationGate: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const token = useKcToken();
  const orgState = useOrganizationState(token);
  // Local override so the UI reflects a just-completed submission immediately,
  // without waiting on a token refresh or re-polling the fallback endpoint.
  const [justSubmitted, setJustSubmitted] =
    useState<ISubmittedOrganization | null>(null);
  const [submittedDocuments, setSubmittedDocuments] = useState<
    IReviewDocument[] | null
  >(null);
  const [submittedDetails, setSubmittedDetails] =
    useState<IReviewDetails | null>(null);

  const organizationCode = justSubmitted?.code ?? orgState.code;
  const isPending = orgState.state === EOrganizationState.PENDING;
  /** Registered in this session: the organization is new, so it has nothing on file yet. */
  const isFreshRegistration = !!justSubmitted && !submittedDocuments;

  // Bank + address a returning PENDING org already saved, from my-organization.
  const savedBank = useMemo(
    () =>
      bankDraftFromSaved(orgState.bankAccount, {
        commercialRegisterNumber: orgState.commercialRegisterNumber,
        unifiedNumber: orgState.unifiedNumber,
      }),
    [
      orgState.bankAccount,
      orgState.commercialRegisterNumber,
      orgState.unifiedNumber,
    ]
  );
  const savedAddress = useMemo(
    () => addressDraftFromSaved(orgState.address),
    [orgState.address]
  );

  // Only a returning user needs looking up — it decides whether they see their documents on the
  // review screen or get asked to upload. A fresh registration is never looked up: there is
  // nothing to find, and letting a lookup result gate the upload step is how the step ended up
  // unreachable before.
  const existing = useOrganizationDocuments(
    organizationCode,
    ORGANIZATION_DOCUMENTS_STEP_ENABLED &&
      !submittedDocuments &&
      isPending &&
      !isFreshRegistration
  );

  const renderGated = (content: React.ReactNode, statusLabel: string) => (
    <OrganizationGateShell
      organizationCode={organizationCode}
      organizationName={justSubmitted?.name ?? orgState.name}
      organizationLogoUrl={justSubmitted?.logoUrl ?? orgState.logoUrl}
      organizationStatusLabel={statusLabel}>
      {content}
    </OrganizationGateShell>
  );

  /**
   * A pending organization completes the activation tasks first, then sees the review screen.
   * On a later visit, what's already on file (bank + address from my-organization, attachments)
   * stands in for the session state: all three on file → review screen, otherwise the
   * activation step with those tasks already done.
   */
  const renderPendingFlow = () => {
    const showPending = (
      documents?: IReviewDocument[],
      details?: IReviewDetails
    ) =>
      renderGated(
        <OrganizationPending documents={documents} details={details} />,
        t("lbl_org_gate_status_pending") as string
      );

    const showActivation = (documentsOnFile?: IReviewDocument[]) =>
      renderGated(
        // organizationCode isn't required to render: gating on it would let a missing code
        // swallow the step. A bad code surfaces as a visible upload error instead.
        <OrganizationActivation
          organizationCode={organizationCode ?? ""}
          savedBank={isFreshRegistration ? null : savedBank}
          savedAddress={isFreshRegistration ? null : savedAddress}
          documentsOnFile={documentsOnFile}
          onSubmitted={(documents, details) => {
            setSubmittedDetails(details);
            setSubmittedDocuments(documents);
          }}
        />,
        t("lbl_org_gate_status_documents") as string
      );

    const savedDetails: IReviewDetails = {
      bank: savedBank ? bankSummary(savedBank) : undefined,
      address: savedAddress ? addressSummary(savedAddress) : undefined,
    };

    // Just submitted — no need to ask the server what we already know.
    if (submittedDocuments) {
      return showPending(submittedDocuments, submittedDetails ?? undefined);
    }

    if (!ORGANIZATION_DOCUMENTS_STEP_ENABLED) {
      return showPending(undefined, savedDetails);
    }

    // Straight to the activation step after registering — no lookup, nothing that can hide it.
    if (isFreshRegistration) return showActivation();

    if (existing.state === EDocumentsState.LOADING) {
      return renderGated(
        <div className={styles.container}>
          <Spinner />
        </div>,
        t("lbl_org_gate_status_pending") as string
      );
    }

    const documentsOnFile = existing.documents.map((doc) => ({
      key: String(doc.id),
      title: doc.title,
      // The original file name is what we store in `description` on upload.
      fileName: doc.description,
    }));

    if (
      existing.state === EDocumentsState.PRESENT &&
      savedBank &&
      savedAddress
    ) {
      return showPending(documentsOnFile, savedDetails);
    }

    // Something is missing (or the lookup failed) — let them complete it.
    return showActivation(documentsOnFile);
  };

  if (justSubmitted) {
    return renderPendingFlow();
  }

  switch (orgState.state) {
    case EOrganizationState.LOADING:
      return (
        <div className={styles.container}>
          <Spinner />
        </div>
      );
    case EOrganizationState.NO_ORGANIZATION:
      return renderGated(<PortalOnboardingPrompt />, "");
    case EOrganizationState.PENDING:
      return renderPendingFlow();
    case EOrganizationState.REJECTED:
      return renderGated(
        <OrganizationRejected
          rejectionReason={orgState.rejectionReason}
          name={orgState.name}
          websiteUrl={orgState.websiteUrl}
          description={orgState.description}
          onResubmit={(organization) =>
            setJustSubmitted(
              organization ?? {
                code: orgState.code ?? "",
                name: orgState.name ?? "",
              }
            )
          }
        />,
        t("lbl_org_gate_status_rejected") as string
      );
    case EOrganizationState.VERIFIED:
    default:
      return <>{children}</>;
  }
};

export default OrganizationGate;
